import { HttpClient } from '@angular/common/http';
import { Injectable, computed, signal } from '@angular/core';
import type { IInstructionCatalog, IInstructionMutationResult, TInstructionKey } from '@common/microservice-types/db/instruction';
import type { IResolvedUserSettings } from '@common/microservice-types/db/settings.user';
import type { IUserSkill } from '@common/microservice-types/db/skill/skill.user';
import { InstructionEditor } from '@common/utils/instruction-editor';
import { environment } from '@env';
import { TranslateService } from '@ngx-translate/core';
import { UserSettingsApiService } from '@services/user-settings-api/user-settings-api.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class InstructionsService
{
    private readonly url = `${environment.office_api_host}/api/v2/r2-chat/instructions`;
    readonly catalog = signal<IInstructionCatalog | null>(null);
    readonly settings = signal<IResolvedUserSettings | null>(null);
    readonly loading = signal(false);
    readonly loadError = signal(false);
    readonly editor: InstructionEditor = new InstructionEditor((state) => this.editorState.set(state));
    readonly editorState = signal(this.editor.state);
    readonly confirmedInstruction$ = new BehaviorSubject<string | null>(null);
    private account: string | null = null;
    private revision = 0;
    private writeRevision = 0;
    private readonly pendingLegacy = signal<string | null>(null);
    private ready: Promise<void> | null = null;

    readonly selectedKey = computed<TInstructionKey>(() => this.editorState().selection
        ?? this.settings()?.instructionKey
        ?? this.catalog()?.instructions.find((item) => item.prompt === this.effectiveInstruction())?.instructionKey
        ?? (this.pendingLegacy()?.trim() ? 'custom' : 'formal'));

    readonly effectiveInstruction = computed<string | null>(() =>
    {
        const settings = this.settings();
        if (!settings?.instructionKey && settings?.instruction?.trim()) return settings.resolvedInstruction ?? settings.instruction;
        if (!settings?.instructionKey && this.pendingLegacy()?.trim()) return this.pendingLegacy();
        const key = settings?.instructionKey ?? 'formal';
        return this.catalog()?.instructions.find((item) => item.instructionKey === key)?.prompt
            ?? settings?.resolvedInstruction ?? null;
    });

    constructor(private http: HttpClient, private api: UserSettingsApiService, private translate: TranslateService) {}

    setAccount(account: string | null): void
    {
        if (account === this.account) return;
        this.account = account;
        this.api.reset();
        this.reset();
    }

    initialize(legacy?: string | null): Promise<void>
    {
        this.pendingLegacy.set(legacy?.trim() ? legacy : null);
        this.ready = this.refresh(false);
        return this.ready;
    }

    async refresh(forceSettings = true): Promise<void>
    {
        const revision = ++this.revision;
        const generation = this.api.generation;
        const writeRevision = this.writeRevision;
        this.loading.set(true);
        let importFailed = false;
        try
        {
            const settings = await this.api.getSettings(forceSettings);
            if (revision !== this.revision || generation !== this.api.generation) return;
            if (settings && writeRevision === this.writeRevision) this.settings.set(settings);
            if (this.pendingLegacy() && !this.editorState().dirty && !this.editorState().saving)
            {
                try
                {
                    const imported = await firstValueFrom(this.http.post<IInstructionMutationResult>(
                        `${this.url}/import-legacy`, { instruction: this.pendingLegacy() },
                    ));
                    if (revision !== this.revision || generation !== this.api.generation) return;
                    this.pendingLegacy.set(null);
                    if (writeRevision === this.writeRevision) this.accept(imported.settings, generation);
                }
                catch { importFailed = true; }
            }
            const locale = settings?.locale?.isoCode ?? this.translate.currentLang ?? 'en';
            const catalog = await firstValueFrom(this.http.get<IInstructionCatalog>(this.url, { params: { locale } }));
            if (revision !== this.revision || generation !== this.api.generation) return;
            if (writeRevision === this.writeRevision) this.catalog.set(catalog);
            else this.catalog.set({ ...catalog, instructions: [
                ...catalog.instructions.filter((item) => item.instructionKey !== 'custom'),
                ...(this.catalog()?.instructions.filter((item) => item.instructionKey === 'custom') ?? []),
            ] });
            this.loadError.set(importFailed);
            this.editor.hydrate(this.catalog()?.instructions.find((item) => item.instructionKey === 'custom')?.prompt ?? this.pendingLegacy() ?? '');
            this.publish();
        }
        catch
        {
            if (revision === this.revision && generation === this.api.generation) { this.loadError.set(true); this.publish(); }
        }
        finally { if (revision === this.revision) this.loading.set(false); }
    }

    editCustom(text: string): void
    {
        const generation = this.api.generation;
        this.writeRevision++;
        this.editor.edit(text, async (prompt) =>
        {
            const result = await firstValueFrom(this.http.put<IInstructionMutationResult>(`${this.url}/custom`, { prompt }));
            if (generation !== this.api.generation) return;
            this.pendingLegacy.set(null);
            this.catalog.update((value) => ({
                requestedLocale: value?.requestedLocale ?? this.translate.currentLang ?? 'en',
                unavailableKeys: value?.unavailableKeys ?? [],
                instructions: [...(value?.instructions ?? []).filter((item) => item.instructionKey !== 'custom'),
                    ...(result.instruction ? [result.instruction] : [])],
            }));
            this.accept(result.settings, generation);
        });
    }

    select(key: TInstructionKey): void
    {
        const generation = this.api.generation;
        this.writeRevision++;
        this.editor.select(key, async () =>
        {
            const settings = await this.api.selectInstruction(key);
            if (generation !== this.api.generation) return;
            this.pendingLegacy.set(null);
            this.accept(settings, generation);
        });
    }

    focusCustom(): void
    {
        if (this.editorState().draft.trim()) this.editCustom(this.editorState().draft);
    }

    flush(): Promise<void> { return this.editor.flush(); }
    retry(): Promise<void> { return this.editor.retry(); }

    async forSend(skill?: IUserSkill | null, accountSettings?: IResolvedUserSettings): Promise<string>
    {
        const generation = this.api.generation;
        await this.ready;
        await this.flush();
        if (generation !== this.api.generation) throw new Error('Instruction context changed');
        const explicitSkill = skill?.instructionMode && skill.instructionMode !== 'inherit';
        const profile = accountSettings ?? this.settings();
        if (generation !== this.api.generation) throw new Error('Instruction context changed');
        let instruction: string | null | undefined;
        const legacySkill = skill && !skill.instructionMode && skill.instruction?.trim();
        const key = explicitSkill ? skill.instructionMode : profile?.instructionKey ?? 'formal';
        if (skill?.instructionMode === 'custom' || legacySkill) instruction = skill.instruction;
        else if (!explicitSkill && !profile?.instructionKey && (profile?.instruction?.trim() || this.pendingLegacy()?.trim()))
            instruction = profile?.instruction?.trim() ? profile.resolvedInstruction ?? profile.instruction : this.pendingLegacy();
        else if (key === 'custom')
            instruction = accountSettings?.resolvedInstruction
                ?? this.catalog()?.instructions.find((item) => item.instructionKey === 'custom')?.prompt
                ?? profile?.resolvedInstruction;
        else
        {
            const locale = accountSettings?.locale?.isoCode ?? this.api.confirmedLocale ?? this.settings()?.locale?.isoCode;
            if (locale && this.catalog() && this.catalog()?.requestedLocale !== locale)
            {
                if (accountSettings?.resolvedInstruction?.trim() && !explicitSkill)
                    instruction = accountSettings.resolvedInstruction;
                else
                {
                    await this.refresh();
                    if (this.catalog()?.requestedLocale !== locale) throw new Error('Instruction locale unavailable');
                }
            }
            if (!accountSettings && locale !== (this.api.confirmedLocale ?? this.settings()?.locale?.isoCode))
                throw new Error('Instruction locale changed');
            instruction ??= this.catalog()?.instructions.find((item) => item.instructionKey === key)?.prompt
                ?? (explicitSkill ? skill?.resolvedInstruction : profile?.resolvedInstruction);
        }
        if (generation !== this.api.generation) throw new Error('Instruction context changed');
        if (!instruction?.trim()) throw new Error('Instruction unavailable. Open settings and retry.');
        return instruction;
    }

    reset(): void
    {
        this.revision++;
        this.writeRevision++;
        this.editor.reset();
        this.catalog.set(null);
        this.settings.set(null);
        this.pendingLegacy.set(null);
        this.ready = null;
        this.loading.set(false);
        this.loadError.set(false);
        this.confirmedInstruction$.next(null);
    }

    private accept(settings: IResolvedUserSettings, generation: number): void
    {
        this.api.acceptInstructionSettings(settings, generation);
        const current = this.settings();
        this.settings.set(current ? {
            ...current,
            instruction: settings.instruction,
            instructionKey: settings.instructionKey,
            customInstruction: settings.customInstruction,
            resolvedInstruction: settings.resolvedInstruction,
        } : settings);
        this.publish();
    }

    private publish(): void { this.confirmedInstruction$.next(this.effectiveInstruction()); }
}
